import React, { useEffect, useRef, useState } from 'react';
import { Box, Divider, Typography, Grid } from '@mui/material';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useLazyQuery } from '@apollo/client';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  IClientObjective,
  addClientRequest,
  ICreateClientInput,
  addClientGroupRequest,
  IClient,
} from '../../redux/client/clientSlice';
import { getGenderFromPronoun } from '../../utils/userInputUtils';
import { ClientType } from '../../types/client';
import { graphLineColors } from '../../utils/styles';
import {
  getValidationSchema,
  initialValues,
  FormValues,
  StaffMember,
  groupClientInitialValues,
  IGroupClientFormValues,
  getGroupValidationSchema,
} from '../../utils/validations/clientDetailsForm';
import AddNewCoachModal from '../AddNewCoach/AddNewCoachModal';
import ClientGroupTypeButtons from './ClientGroupTypeButtons';
import AddSingleClient from './AddSingleClient';
import AddClientGroup from './AddClientGroup';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';
import { ICoachGroup, IGetCoachGroupPayload } from '../../redux/coachGroup/coachGroupSlice';
import { GET_COACH_GROUP } from '../../service/graphql/coachGroupQuery';
import { GET_CLIENT_OBJECTIVES } from '../../service/graphql/clientsQuery';

export interface IAddClientProps {
  isOpen: boolean;
  onClose: () => void;
  isClientGroupPath?: boolean;
  clientGroupId?: string;
}

function AddClient({
  isOpen,
  onClose,
  isClientGroupPath = false,
  clientGroupId,
}: Readonly<IAddClientProps>) {
  const dispatch = useAppDispatch();
  const coach = useAppSelector((state) => state.coach.coach);
  const coachId = coach?.id || '';
  const objectivesInputRef = useRef<HTMLInputElement>(null);
  const [isObjectivesTouched, setIsObjectivesTouched] = useState(false);
  const [alignment, setAlignment] = React.useState('individual');
  const fixedOptions = [
    { id: coach.id, name: t('common.me'), email: coach.email, profilePic: coach.profilePic },
  ];
  const [options, setOptions] = useState([...fixedOptions]);
  const [lastObjective, setLastObjective] = useState<string | undefined>();
  const [currentSkip, setCurrentSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const { addClientLoading } = useAppSelector((state) => state.client);
  const [groupName, setGroupName] = useState('');
  const [stepNumber, setStepNumber] = useState(0);
  const [getCoachGroup, { loading: getCoachGroupLoading, data }] = useLazyQuery<
    { getCoachGroup: ICoachGroup },
    { getCoachGroupInput: IGetCoachGroupPayload }
  >(GET_COACH_GROUP, {
    fetchPolicy: 'network-only',
  });
  const [getClientObjectives, { loading: clientObjectivesLoading, data: objectivesData }] =
    useLazyQuery<{
      getClientById: Pick<IClient, 'objectives' | 'clientType'>;
    }>(GET_CLIENT_OBJECTIVES);

  useEffect(() => {
    if (isClientGroupPath && clientGroupId) {
      getClientObjectives({
        variables: { id: clientGroupId },
        fetchPolicy: 'network-only',
      });
    }
  }, [isClientGroupPath, clientGroupId, coachId]);

  const coachGroup = data?.getCoachGroup || {
    coaches: [],
    totalCount: 0,
    id: '',
    principalCoach: null,
  };

  const loadMoreOptions = () => {
    if (getCoachGroupLoading || !hasMore) return;
    getCoachGroup({
      variables: { getCoachGroupInput: { skip: currentSkip, take: 4, isactiveCoaches: true } },
    });
  };
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;

  const handleAddClient = (values: FormValues) => {
    let objectives: IClientObjective[] | null;
    if (values.objectives && values.objectives.length > 0) {
      objectives = values.objectives.map((tag, index) => ({
        objectiveName: tag,
        colorCode: graphLineColors[index],
      }));
    } else if (objectivesInputRef.current?.value.trim()) {
      objectives = [
        {
          objectiveName: objectivesInputRef.current?.value.trim(),
          colorCode: graphLineColors[0],
        },
      ];
    } else {
      objectives = null;
    }

    let validDob: string | undefined;
    if (values.dob && dayjs(values.dob).isValid()) {
      validDob = values.dob;
    }
    let formattedCoaches: string[] = [];
    if (Array.isArray(values?.staffMembers) && values?.staffMembers.length) {
      formattedCoaches = values.staffMembers.filter((e) => e.id !== coachId).map((c) => c.id);
    }
    const clientInput: ICreateClientInput = {
      CreateClient: {
        coachId,
        clientGroupId,
        coaches: formattedCoaches,
        client: {
          firstName: values.firstName,
          lastName: null,
          surname: `${values.firstName} ${values.lastName}`,
          email: values.email,
          profilePic: '',
          dob: validDob,
          gender: getGenderFromPronoun(values.pronoun),
          tp: values.phoneNumber,
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          pronoun: values.pronoun ?? null,
          organizationId: 'decb4748-9dba-4672-b0c2-88aad43fc307',
          archived: false,
          objectives,
        },
      },
    };
    dispatch(addClientRequest(clientInput));
  };

  const handleAddGroup = (values: IGroupClientFormValues) => {
    const { description, objectives, staffMembers, members } = values;
    const formattedObjectives = objectives.map((objective, index) => ({
      objectiveName: objective,
      colorCode: graphLineColors[index % graphLineColors.length],
    }));

    const formattedCoaches = staffMembers.filter((e) => e.id !== coachId).map((c) => c.id);
    const coachGroupId = coachGroup.id;
    const formattedClients = members.map((member) => ({
      firstName: member.name.split(' ')[0],
      lastName: member.name.split(' ')[1] || null,
      email: member.email,
      dob: member.dob || null,
      tp: member.phoneNumber || '',
      gender: '',
      objectives: formattedObjectives,
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      surname: member.name,
      archived: false,
      profilePic: '',
      organizationId: 'decb4748-9dba-4672-b0c2-88aad43fc307',
    }));

    const groupPayload = {
      client: {
        firstName: groupName,
        description: description || null,
        objectives: formattedObjectives,
        organizationId: 'decb4748-9dba-4672-b0c2-88aad43fc307',
        email: values.staffMembers[0].email,
        profilePic: '',
        gender: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        surname: groupName,
        archived: false,
        tp: '',
      },
      coaches: formattedCoaches,
      principalCoach: coachId,
      coachGroupId,
      clients: formattedClients,
      clientType: ClientType.GROUP,
    };

    dispatch(addClientGroupRequest(groupPayload));
  };

  const formikIndividual = useFormik<FormValues>({
    initialValues,
    validationSchema: getValidationSchema(isObjectivesTouched),
    onSubmit: (values) => handleAddClient(values),
  });

  const formikGroup = useFormik<IGroupClientFormValues>({
    initialValues: groupClientInitialValues,
    validationSchema: getGroupValidationSchema(isObjectivesTouched, stepNumber),
    onSubmit: (values) => handleAddGroup(values),
  });

  useEffect(() => {
    const formik = alignment === 'individual' ? formikIndividual : formikGroup;
    formik.setFieldValue('objectives', []);
    formik.setFieldValue('staffMembers', fixedOptions);
    if (isOpen) {
      setOptions([...fixedOptions]);
      setCurrentSkip(0);
      setHasMore(true);
      getCoachGroup({
        variables: { getCoachGroupInput: { skip: 0, take: 4, isactiveCoaches: true } },
      });
    }

    return () => {
      setOptions([...fixedOptions]);
      setCurrentSkip(0);
      setHasMore(true);
    };
  }, [isOpen]);

  useEffect(() => {
    let { coaches } = coachGroup;
    if (!coach.isPrincipalCoach) {
      coaches = coaches.filter((c) => c.id !== coach?.id);
    }
    if (coaches.length > 0) {
      setCurrentSkip((prev) => prev + 4);
      const mappedCoaches = coaches.map((c) => ({
        id: c.id,
        name: `${c.firstName} ${c.lastName || ''}`,
        email: c.email,
        profilePic: c.profilePic,
      }));
      setOptions((prevOptions) => [...prevOptions, ...mappedCoaches]);
    }
  }, [coachGroup.coaches]);

  useEffect(() => {
    if (coachGroup.totalCount > 0 && coachGroup.totalCount + 1 <= options.length) {
      setHasMore(false);
    }
  }, [coachGroup.totalCount, options]);

  useEffect(() => {
    const formik = alignment === 'individual' ? formikIndividual : formikGroup;
    formik.setFieldValue('staffMembers', fixedOptions);
  }, [alignment]);

  const handleTagsChange = (newValue: (string | object)[]) => {
    const formik = alignment === 'individual' ? formikIndividual : formikGroup;
    const uniqueTags = Array.from(
      new Set(newValue.map((tag) => (typeof tag === 'string' ? tag.trim() : '')))
    );
    const isTagTooLong = uniqueTags.some((tag) => tag.length > 50);
    if (isTagTooLong) {
      return;
    }
    if (uniqueTags.length > 0) {
      formik.values.objectives = uniqueTags;
    } else {
      formik.values.objectives = [];
    }
    formik.setFieldTouched('objectives', true); // Manually set touched state
    setLastObjective(undefined);
    formik.validateField('objectives');
  };

  const handleStaffTagsChange = (newValue: (string | object)[]) => {
    const formik = alignment === 'individual' ? formikIndividual : formikGroup;
    formik.setFieldTouched('staffMembers', true);
    const idsNew = newValue.map((v) => (typeof v === 'object' && 'id' in v ? v.id : ''));
    if (
      formik.values.staffMembers &&
      newValue.length < formik.values.staffMembers.length &&
      idsNew.includes(fixedOptions[0].id)
    ) {
      formik.setFieldValue('staffMembers', newValue);
    } else {
      const ids = formik.values.staffMembers?.map((v) => v.id);
      const uniqueValues = [...newValue.filter((e) => !ids?.includes((e as StaffMember).id))];
      formik.setFieldValue('staffMembers', [
        ...(formik.values.staffMembers || []),
        ...uniqueValues,
      ]);
    }
  };

  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 100);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const formik = alignment === 'individual' ? formikIndividual : formikGroup;
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      const inputValue = (event.target as HTMLInputElement).value.trim();
      if (inputValue) {
        const isTagTooLong = inputValue.length > 50;
        if (isTagTooLong) {
          formik.setFieldError('objectives', t('addNewClient.isTagTooLong'));
          return;
        }
        if (formik.values.objectives.includes(inputValue)) {
          formik.setFieldError('objectives', t('addNewClient.duplicate'));
        } else if (formik.values.objectives.length >= 6) {
          formik.setFieldError('objectives', t('addNewClient.max'));
        } else {
          handleTagsChange([...formik.values.objectives, inputValue]);
          formik.setFieldTouched('objectives', true);
        }
      }
    }
  };

  const handleObjectivesBlur = (e: React.FocusEvent<HTMLDivElement, Element>) => {
    const formik = alignment === 'individual' ? formikIndividual : formikGroup;
    if (lastObjective) {
      handleTagsChange([...formik.values.objectives, lastObjective]);
    }
    formik.handleBlur(e);
  };

  const triggerTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLastObjective(e.target.value);
    if (e.target.value) {
      setIsObjectivesTouched(true);
    } else {
      setIsObjectivesTouched(false);
    }
  };

  const handleStepCompletion = (newGroupName: string, step: number) => {
    setGroupName(newGroupName);
    setStepNumber(step);
  };

  useEffect(() => {
    if (
      isClientGroupPath &&
      !clientObjectivesLoading &&
      objectivesData?.getClientById?.clientType === ClientType.GROUP &&
      objectivesData?.getClientById?.objectives?.length
    ) {
      const { objectives } = objectivesData.getClientById;
      handleTagsChange(objectives.map((i) => i.objectiveName));
    }
  }, [clientObjectivesLoading, objectivesData]);

  return (
    <AddNewCoachModal
      isOpen={isOpen}
      xs={alignment === 'group' ? 11.5 : undefined}
      md={alignment === 'group' ? 8.5 : undefined}
    >
      <Grid item xs={12} bgcolor="background.paper" p="32px">
        <Grid gap="36px" display="flex" flexDirection="column">
          {/* first row  */}
          <Box display="flex" justifyContent="space-between">
            <Typography variant="h4" color="primary">
              {groupName !== '' && stepNumber !== 0 ? groupName : t('topbar.addNewClient')}
            </Typography>
            <img
              src="/assets/icons/close-circle-large.svg"
              alt="close"
              onClick={onClose}
              role="presentation"
              style={{ cursor: 'pointer' }}
            />
          </Box>
          <Divider />
          {/* second row */}
          <Box gap={2} display="flex" flexDirection="column">
            {/* third row */}
            {stepNumber === 0 &&
              !isClientGroupPath &&
              checkPermissions([UserPermissionType.CLIENT_GROUP_MODULE_EDIT_CLIENT_GROUP]) && (
                <>
                  <Box display="flex">{ClientGroupTypeButtons({ alignment, setAlignment })}</Box>
                  <Divider />
                </>
              )}
            {alignment === 'individual' &&
            checkPermissions([UserPermissionType.CORE_MODULE_ADD_NEW_CLIENT]) ? (
              <AddSingleClient
                formik={formikIndividual}
                triggerTextChange={triggerTextChange}
                handleTagsChange={handleTagsChange}
                handleKeyDown={handleKeyDown}
                handleObjectivesBlur={handleObjectivesBlur}
                handleStaffTagsChange={handleStaffTagsChange}
                loadMoreOptions={loadMoreOptions}
                options={options}
                fixedOptions={fixedOptions}
                objectivesInputRef={objectivesInputRef}
                addClientLoading={addClientLoading}
                noCoaches={isClientGroupPath}
                noObjectives={isClientGroupPath}
              />
            ) : (
              checkPermissions([UserPermissionType.CLIENT_GROUP_MODULE_EDIT_CLIENT_GROUP]) && (
                <AddClientGroup
                  formik={formikGroup}
                  triggerTextChange={triggerTextChange}
                  handleTagsChange={handleTagsChange}
                  handleKeyDown={handleKeyDown}
                  handleObjectivesBlur={handleObjectivesBlur}
                  handleStaffTagsChange={handleStaffTagsChange}
                  loadMoreOptions={loadMoreOptions}
                  options={options}
                  fixedOptions={fixedOptions}
                  objectivesInputRef={objectivesInputRef}
                  onStepComplete={handleStepCompletion}
                  activeStep={stepNumber}
                />
              )
            )}
          </Box>
        </Grid>
      </Grid>
    </AddNewCoachModal>
  );
}
export default AddClient;
